import { Pipe, PipeTransform } from "@angular/core";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import * as marked from "marked";
import { Renderer } from "marked";

@Pipe({
  name: "markdown",
})
export class MarkdownPipe implements PipeTransform {
  private renderer: Renderer;

  constructor(private sanitizer: DomSanitizer) {
    this.renderer = this.buildRenderer();
  }

  transform(value: string): SafeHtml {
    if (!value) {
      return "";
    }

    let md = this.stripCopilotLinkDefs(value);
    md = this.convertCiteRefs(md);
    md = this.convertPseudoHeadings(md);

    // marked 0.4.0 uses function call, not marked.parse()
    const html = marked(md, {
      renderer: this.renderer,
    });

    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  private stripCopilotLinkDefs(md: string): string {
    return md
      .replace(/^\[\d+\]:\s*cite:\d+\s+"[^"]*"\s*$/gm, "")
      .replace(/\s+$/, "");
  }

  private convertCiteRefs(md: string): string {
    return md.replace(/\[(\d+)\](?![\(:])/g, function (match, num) {
      return (
        `<button class="md-cite-ref" data-cite="${num}" type="button" ` +
        `aria-label="View source ${num}">${num}</button>`
      );
    });
  }

  private convertPseudoHeadings(md: string): string {
    return md.replace(
      /^(?![ \t]*[-*+][ \t]|[ \t]*\d+\.[ \t])[ \t]*\*\*([^*\n]+?)\*\*[ \t]*$/gm,
      function (match, inner) {
        return `\n<div class="md-pseudo-heading">${inner.trim()}</div>\n`;
      },
    );
  }

  private buildRenderer(): Renderer {
    const renderer = new Renderer();

    // marked 0.4.0 signature:
    // heading(text, level, raw)
    renderer.heading = function (text: string, level: number): string {
      const tag = "h" + Math.min(level + 2, 6);

      return `<${tag} class="md-h">${text}</${tag}>`;
    };

    // marked 0.4.0 signature:
    // paragraph(text)
    renderer.paragraph = function (text: string): string {
      if (
        text &&
        text.trim().indexOf('<div class="md-pseudo-heading">') === 0
      ) {
        return text;
      }

      return `<p class="md-p">${text}</p>`;
    };

    // marked 0.4.0 signature:
    // list(body, ordered)
    renderer.list = function (body: string, ordered: boolean): string {
      const tag = ordered ? "ol" : "ul";
      const cls = ordered ? "md-ol" : "md-ul";

      return `<${tag} class="${cls}">${body}</${tag}>`;
    };

    // marked 0.4.0 signature:
    // listitem(text)
    renderer.listitem = function (text: string): string {
      return `<li>${text}</li>`;
    };

    renderer.codespan = function (text: string): string {
      return `<code class="md-code">${text}</code>`;
    };

    // marked 0.4.0 signature:
    // code(code, language)
    renderer.code = function (code: string, language: string): string {
      const cls = language ? ` class="language-${language}"` : "";

      return `<pre class="md-pre"><code${cls}>${code}</code></pre>`;
    };

    renderer.hr = function (): string {
      return '<hr class="md-hr">';
    };

    // marked 0.4.0 signature:
    // link(href, title, text)
    renderer.link = function (
      href: string,
      title: string,
      text: string,
    ): string {
      if (href && href.indexOf("cite:") === 0) {
        const num = href.replace("cite:", "");

        return (
          `<button class="md-cite-ref" data-cite="${num}" type="button" ` +
          `aria-label="View source ${num}">${num}</button>`
        );
      }

      return `<a href="${href}" target="_blank" rel="noopener noreferrer">${text}</a>`;
    };

    // marked 0.4.0 signature:
    // blockquote(quote)
    renderer.blockquote = function (quote: string): string {
      return `<blockquote class="md-blockquote">${quote}</blockquote>`;
    };

    return renderer;
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/pipes/markdown.pipe.ts